import React, { useEffect, useRef, useState } from 'react';
import { Card, Typography, Tag, Spin, theme } from 'antd';
import { DownloadOutlined, PictureOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { getCustomerMediaBlobUrl, downloadCustomerMedia } from '../../services/workspaceMediaService';

dayjs.extend(relativeTime);

const { Text } = Typography;

const isImageExt = (ext) =>
  ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(String(ext || '').toLowerCase());

function formatFileSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v < 10 && i > 0 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

function extensionBadgeLabel(extRaw) {
  const ext = String(extRaw || '').toLowerCase();
  if (!ext) return 'FILE';
  if (ext === 'jpeg') return 'JPG';
  return ext.length <= 4 ? ext.toUpperCase() : ext.slice(0, 4).toUpperCase();
}

function FileTypeBadge({ extension, token, box = 44 }) {
  const ext = String(extension || '').toLowerCase();
  const label = extensionBadgeLabel(extension);
  const isPdf = ext === 'pdf';
  const innerBg = isPdf ? token.colorError : token.colorPrimary;
  const haloBg = isPdf ? token.colorErrorBg : token.colorPrimaryBg;
  return (
    <span
      className="workspace-file-type-halo"
      style={{
        display: 'inline-flex',
        padding: token.paddingXXS ?? 2,
        borderRadius: token.borderRadiusLG,
        background: haloBg,
        lineHeight: 0,
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          width: box,
          height: box,
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: token.borderRadius,
          background: innerBg,
          color: token.colorTextLightSolid,
          fontSize: label.length > 3 ? 10 : 11,
          fontWeight: 600,
          letterSpacing: 0.02,
        }}
      >
        {label}
      </span>
    </span>
  );
}

const WorkspaceFileCard = ({ media, onPreview }) => {
  const { token } = theme.useToken();
  const visibility = media.visibility || 'internal';
  const creator = media.extra?.creator;
  const portalUploader = media.extra?.portal_uploader;
  const uploaderName =
    creator?.extra?.full_name ||
    [creator?.first_name, creator?.last_name].filter(Boolean).join(' ') ||
    portalUploader?.full_name ||
    'Unknown';

  const [thumb, setThumb] = useState(null);
  const [thumbLoading, setThumbLoading] = useState(false);
  const thumbRef = useRef(null);

  const thumbBox = 52;
  const isImage = isImageExt(media.extension);

  useEffect(() => {
    let cancelled = false;
    const revokeCurrent = () => {
      const u = thumbRef.current;
      if (u && u.startsWith('blob:')) {
        window.URL.revokeObjectURL(u);
      }
      thumbRef.current = null;
    };

    const load = async () => {
      revokeCurrent();
      setThumb(null);
      if (!isImageExt(media.extension)) {
        setThumbLoading(false);
        return;
      }
      setThumbLoading(true);
      const url = await getCustomerMediaBlobUrl(media.id);
      if (cancelled) {
        if (url && url.startsWith('blob:')) {
          window.URL.revokeObjectURL(url);
        }
        return;
      }
      thumbRef.current = url;
      setThumb(url);
      setThumbLoading(false);
    };
    load();
    return () => {
      cancelled = true;
      revokeCurrent();
      setThumb(null);
    };
  }, [media.id, media.extension]);

  const handleDownload = async (e) => {
    e?.stopPropagation?.();
    await downloadCustomerMedia(media.id, media.display_name);
  };

  const handlePreview = () => {
    if (typeof onPreview === 'function') {
      onPreview();
    }
  };

  const thumbInner =
    thumbLoading ? (
      <Spin size="small" />
    ) : thumb && isImage ? (
      <img src={thumb} alt="" className="workspace-file-card-thumb-img" />
    ) : isImage ? (
      <span
        className="workspace-file-type-halo"
        style={{
          display: 'inline-flex',
          padding: token.paddingXXS ?? 2,
          borderRadius: token.borderRadiusLG,
          background: token.colorSuccessBg,
          lineHeight: 0,
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            width: thumbBox - 8,
            height: thumbBox - 8,
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: token.borderRadius,
            background: token.colorSuccess,
            color: token.colorTextLightSolid,
          }}
        >
          <PictureOutlined style={{ fontSize: 22 }} />
        </span>
      </span>
    ) : (
      <FileTypeBadge extension={media.extension} token={token} box={thumbBox - 8} />
    );

  const fileRow = (
    <div className="workspace-file-card-row">
      <button
        type="button"
        className="workspace-file-card-thumb-trigger"
        onClick={handlePreview}
        disabled={!onPreview}
        aria-label="Preview file"
      >
        <span
          className="workspace-file-card-thumb-frame"
          style={{
            width: thumbBox,
            height: thumbBox,
            borderRadius: token.borderRadiusLG,
            border: `1px solid ${token.colorBorderSecondary}`,
            background: token.colorFillQuaternary,
          }}
        >
          {thumbInner}
        </span>
      </button>
      <div className="workspace-file-card-info">
        <div className="workspace-file-card-title-row">
          <Text strong ellipsis={{ tooltip: media.display_name }} className="workspace-file-card-name">
            {media.display_name}
          </Text>
        </div>
        <Text type="secondary" className="workspace-file-card-size">
          {formatFileSize(media.file_size)}
        </Text>
        <Text type="secondary" className="workspace-file-card-uploader">
          {uploaderName}
          {media.created_at ? ` · ${dayjs(media.created_at).fromNow()}` : ''}
        </Text>
      </div>
      <button
        type="button"
        className="workspace-file-card-download"
        onClick={(e) => void handleDownload(e)}
        aria-label="Download file"
      >
        <DownloadOutlined />
      </button>
    </div>
  );

  return (
    <Card
      size="small"
      className="workspace-feed-card workspace-file-card"
      styles={{
        body: { padding: `${token.paddingSM}px ${token.padding}px` },
      }}
      style={{
        marginBottom: 'var(--ant-margin-sm)',
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusLG,
        background: token.colorBgContainer,
      }}
    >
      {fileRow}
    </Card>
  );
};

export default WorkspaceFileCard;
